import styled from 'styled-components';

import { portPillBodyCss } from './portPillTheme';

export const OutputRowHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 0 2px;
`;

export const OutputRowSectionHead = styled.span`
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${({ theme: { colors = {} } }) =>
    colors.onSurfaceVariant ?? '#A1A3B8'};
`;

export const OutputRowAddBtn = styled.button`
  width: 22px;
  height: 22px;
  border: none;
  background: transparent;
  color: ${({ theme: { colors = {} } }) =>
    colors.onSurfaceVariant ?? '#A1A3B8'};
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 120ms, color 120ms;
  padding: 0;

  &:hover {
    background: color-mix(
      in oklch,
      ${({ theme: { colors = {} } }) => colors.primary ?? '#C1C1FF'} 14%,
      transparent
    );
    color: ${({ theme: { colors = {} } }) => colors.primary ?? '#C1C1FF'};
  }
`;

export const OutputRowList = styled.ul`
  list-style: none;
  margin: 0 -18px;
  padding: 0 0 6px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  position: relative;
`;

export const OutputRowItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const OutputRowNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  position: relative;
  padding: 0 18px;
`;

export const OutputRowRemoveBtn = styled.button`
  width: 22px;
  height: 22px;
  border: none;
  background: transparent;
  color: ${({ theme: { colors = {} } }) =>
    colors.onSurfaceVariant ?? '#A1A3B8'};
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 120ms, color 120ms;
  padding: 0;

  &:hover {
    background: color-mix(in oklch, var(--error, #ff5050) 14%, transparent);
    color: var(--error, #ff8a8a);
  }
`;

export const OutputRowNameInput = styled.input`
  flex: 1;
  min-width: 0;
  font: 500 13px/1.35
    'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
  background: ${({ theme: { colors = {} } }) =>
    colors.surfaceContainerHigh ?? '#1F2030'};
  border: 1px solid
    ${({ theme: { colors = {} } }) => colors.outlineVariant ?? '#25263A'};
  border-radius: 999px;
  padding: 7px 14px;
  outline: none;
  transition: border-color 120ms ease, background 120ms ease;

  &:focus {
    border-color: ${({ theme: { colors = {} } }) =>
      colors.primary ?? '#C1C1FF'};
  }

  &::placeholder {
    color: #676680;
    opacity: 1;
  }
`;

export const OutputRowInstr = styled.textarea`
  width: auto;
  box-sizing: border-box;
  margin: 0 18px;
  font: 400 12px/1.4
    'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
  background: ${({ theme: { colors = {} } }) =>
    colors.surfaceContainerHigh ?? '#1F2030'};
  border: 1px solid
    ${({ theme: { colors = {} } }) => colors.outlineVariant ?? '#25263A'};
  border-radius: 14px;
  padding: 8px 14px;
  min-height: 38px;
  max-height: 120px;
  resize: vertical;
  outline: none;
  transition: border-color 120ms ease, background 120ms ease;

  &:focus {
    border-color: ${({ theme: { colors = {} } }) =>
      colors.primary ?? '#C1C1FF'};
  }

  &::placeholder {
    color: #676680;
    opacity: 1;
  }
`;

/**
 * Wrapper around the per-output {@link ArtifactDownloadList}. Matches
 * the horizontal padding of {@link OutputRowInstr} so the artifact
 * rows visually nest under their declared output's instructions.
 */
export const OutputArtifactsWrap = styled.div`
  margin: 4px 18px 0;
`;

/**
 * Wrapper around the per-output artifact-kind picker. Sits between the
 * name + handle row and the instruction textarea, using the same
 * horizontal inset as {@link OutputRowInstr} (and matching the visual
 * left-edge of {@link OutputRowNameInput} above) so the trio of
 * controls forms a clean vertical column. The vertical margin gives
 * the kind picker breathing room from its neighbours instead of
 * crowding the name pill and instruction textarea.
 *
 * <p>Layout matches the stacked {@code FieldLabel + SelectBtn} pattern
 * used by {@link ConfigFieldRenderer} (Input "Data type", etc.) so
 * Task per-port pickers and node body fields are visually
 * indistinguishable.</p>
 */
export const OutputRowKindRow = styled.div`
  margin: 6px 18px 2px 28px;
`;

export const OutputRowLabel = styled.span`
  flex: 1;
  min-width: 0;
  font: 500 13px/1.35
    'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
  background: ${({ theme: { colors = {} } }) =>
    colors.surfaceContainerHigh ?? '#1F2030'};
  border: 1px solid
    ${({ theme: { colors = {} } }) => colors.outlineVariant ?? '#25263A'};
  border-radius: 999px;
  padding: 7px 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const OutputRowPill = styled.div`
  ${portPillBodyCss(-11)}
`;
